import axios from 'axios'
import { useStytch, useStytchSession } from '@stytch/react'
import { useMemo } from 'react'
import { useQuery } from 'react-query'
import {
  PrescriptionCheckins,
  QueryKey,
} from 'api/prescription-checkins/routes'

export type PrescriptionCheckin = {
  id: string
  status: 'complete' | 'incomplete'
  createdAt: string
  provider: {
    id: string
    firstName: string
    lastName: string
    email: string
    credentials: string
    type: string
    sourceHealthId: string
    isActive: boolean
    headshot: string
  }
}

interface UsePrescriptionCheckinsInterface {
  isLoading: boolean
  incompleteCheckin: PrescriptionCheckin
  submitPrescriptionCheckin: (params: {
    id: string
    comment?: string
    medicationFeeling: string
  }) => Promise<any>
  refetch: () => void
}

const usePrescriptionCheckins = (): UsePrescriptionCheckinsInterface => {
  const stytch = useStytch()
  const { session } = useStytchSession()

  const fetchPrescriptionCheckins = async () => {
    if (!session) {
      return []
    }

    const tokens = stytch.session.getTokens()
    const accessToken = tokens ? tokens.session_jwt : undefined

    return fetch(
      `${process.env.REACT_APP_SERVER_URL}${PrescriptionCheckins}?status=incomplete&sortBy=createdAt`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    )
      .then(async (res) => {
        const response = await res.json()

        if (res.ok) {
          return response.data || response
        } else {
          return []
        }
      })
      .catch(() => {
        return []
      })
  }

  const { isLoading, data, refetch } = useQuery(
    QueryKey,
    fetchPrescriptionCheckins
  )

  const incompleteCheckin = useMemo(() => {
    const checkins = data ?? []
    return checkins.length > 0 ? checkins[0] : null
  }, [data])

  const submitPrescriptionCheckin = async ({
    id,
    comment,
    medicationFeeling,
  }: {
    id: string
    comment?: string
    medicationFeeling: string
  }) => {
    if (!session) {
      return null
    }

    const tokens = stytch.session.getTokens()
    const accessToken = tokens ? tokens.session_jwt : undefined
    return axios.patch(
      `${process.env.REACT_APP_SERVER_URL}${PrescriptionCheckins}/${id}`,
      {
        additionalCommentsAnswer: comment,
        feelingRatingAnswer: medicationFeeling,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
  }

  return {
    isLoading,
    incompleteCheckin,
    submitPrescriptionCheckin,
    refetch,
  }
}

export { usePrescriptionCheckins }
